'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge } from '@core/ui/primitives/badge';
import { Button } from '@core/ui/primitives/button';
import { Card, CardHeader, CardTitle, CardContent } from '@core/ui/primitives/card';
import { Input } from '@core/ui/primitives/input';
import { Label } from '@core/ui/primitives/label';
import { Separator } from '@core/ui/primitives/separator';
import { Spinner } from '@core/ui/primitives/spinner';
import { getCachedVaultDetails, setCachedVaultDetails } from '@vaults/ui/management/ClientCacheStore';
import { useToast } from '@/core/ui/hooks/use-toast';

const WIKI_LABELS: Record<string, string> = {
  'azure-wiki': 'Azure DevOps Wiki',
  standard: 'Standard',
};

// Reindex and refresh state interface for parent communication
interface ReindexState {
  canReindex: boolean;
  isReindexing: boolean;
  reindexPercent: number;
  isReindexStuck: boolean;
  onReindex: () => void;
  onCancelReindex: () => void;
  // Refresh state
  canRefresh: boolean;
  isRefreshing: boolean;
  onRefresh: () => void;
}

interface RepositorySyncProps {
  details?: unknown;
  vaultId?: string | null;
  vaultPrivileges?: string[];
  vaultRole?: string | null;
  userIsReadOnly?: boolean;
  onReady?: (opts?: { immediate?: boolean }) => void;
  onReindexStateChange?: (state: ReindexState | null) => void;
}

export function RepositorySync({ vaultId, vaultPrivileges, vaultRole, userIsReadOnly, onReady, onReindexStateChange }: RepositorySyncProps) {
  const { toast } = useToast();
  const cached = vaultId ? getCachedVaultDetails(vaultId) : undefined;
  const seededFromCacheRef = useRef<boolean>(Boolean(cached));

  const [repoUrl, setRepoUrl] = useState('');
  const [personalAccessToken, setPersonalAccessToken] = useState('');
  const [detectedWiki, setDetectedWiki] = useState<string | null>(() => cached?.detectedSourceWiki ?? null);
  const [loading, setLoading] = useState(false);
  const [linkedRepoUrl, setLinkedRepoUrl] = useState<string | null>(() => cached?.repoUrl ?? null);
  const [mode, setMode] = useState<'managed' | 'synced' | null>(() => {
    if (!cached?.mode) return null;
    return cached.mode.toLowerCase() === 'synced' ? 'synced' : 'managed';
  });
  const [credToken, setCredToken] = useState('');
  const [credExpiry, setCredExpiry] = useState('');
  const [webhookUrl, setWebhookUrl] = useState<string | null>(null);
  const [webhookSecret, setWebhookSecret] = useState<string | null>(null);
  const [hasWebhookSecret, setHasWebhookSecret] = useState<boolean>(false);
  const [vaultSlug, setVaultSlug] = useState<string | null>(() => cached?.slug ?? null);
  const [orgSlug, setOrgSlug] = useState<string | null>(() => cached?.organizationUrlSlug ?? null);
  const [reindexJobIds, setReindexJobIds] = useState<string[] | null>(null);
  const [reindexing, setReindexing] = useState<boolean>(false);
  const [reindexPercent, setReindexPercent] = useState<number>(0);
  const [reindexStuck, setReindexStuck] = useState<boolean>(false);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const reindexStartTimeRef = useRef<number | null>(null);
  const [initializing, setInitializing] = useState<boolean>(() => Boolean(vaultId && !cached));
  const [isReadOnly, setIsReadOnly] = useState<boolean>(false);
  const readyRef = useRef<boolean>(false);

  const detectedWikiLabel = useMemo(() => {
    if (!detectedWiki) return 'Not detected yet';
    return WIKI_LABELS[detectedWiki] ?? detectedWiki;
  }, [detectedWiki]);

  // Write operations are blocked when vault or user is read-only
  const canWrite = useMemo(() => {
    return !isReadOnly && !userIsReadOnly;
  }, [isReadOnly, userIsReadOnly]);

  // Reindex requires vault:settings privilege and write access
  const canReindex = useMemo(() => {
    if (!canWrite) return false;
    return vaultPrivileges?.includes('vault:settings') ?? false;
  }, [vaultPrivileges, canWrite]);

  // Refresh requires vault:settings privilege and write access
  const canRefresh = useMemo(() => {
    if (!canWrite) return false;
    return vaultPrivileges?.includes('vault:settings') ?? false;
  }, [vaultPrivileges, canWrite]);

  // URL Validation
  const isValidUrl = useMemo(() => {
    if (!repoUrl) return false;
    try {
      const url = new URL(repoUrl);
      return ['http:', 'https:'].includes(url.protocol);
    } catch {
      return false;
    }
  }, [repoUrl]);

  useEffect(() => {
    async function loadVault() {
      if (!vaultId) {
        setInitializing(false);
        return;
      }
      setInitializing(true);
      try {
        const response = await fetch(`/api/vaults/${vaultId}`);
        if (!response.ok) return;
        const data = await response.json().catch(() => null);
        if (data?.vault?.detectedSourceWiki) {
          setDetectedWiki(data.vault.detectedSourceWiki);
        }
        if (typeof data?.vault?.slug === 'string') setVaultSlug(data.vault.slug);
        // API returns nested organization: { slug, urlSlug }
        const orgUrlSlug = data?.vault?.organization?.urlSlug ?? data?.vault?.organization?.slug;
        if (typeof orgUrlSlug === 'string') setOrgSlug(orgUrlSlug);
        if (typeof data?.vault?.repoUrl === 'string' && data.vault.repoUrl) {
          setLinkedRepoUrl(data.vault.repoUrl);
        } else {
          setLinkedRepoUrl(null);
        }
        if (typeof data?.vault?.mode === 'string') {
          setMode(data.vault.mode.toLowerCase() === 'synced' ? 'synced' : 'managed');
        }
        // Track read-only state to gate write operations client-side
        // Use combined readOnly (vault || org) from server response
        setIsReadOnly(Boolean(data?.vault?.readOnly ?? data?.vault?.isReadOnly));

        // Sync cache with latest vault details
        if (vaultId) {
          setCachedVaultDetails(vaultId, {
            slug: data?.vault?.slug,
            organizationUrlSlug: orgUrlSlug ?? null,
            repoUrl: data?.vault?.repoUrl ?? null,
            mode: data?.vault?.mode ?? null,
            detectedSourceWiki: data?.vault?.detectedSourceWiki ?? null,
          });
        }

        // Load webhook info for linked repos
        if (data?.vault?.repoUrl) {
          try {
            const res = await fetch(`/api/vaults/${vaultId}/sync/webhook/info`, { cache: 'no-store' });
            const info = await res.json().catch(() => null);
            if (res.ok && info) {
              setWebhookUrl(info.webhookUrl || null);
              setHasWebhookSecret(Boolean(info.hasSecret));
            }
          } catch {}
        } else {
          setWebhookUrl(null);
          setHasWebhookSecret(false);
        }
      } catch (error) {
        console.warn('[RepositoryImport] failed to load vault', error);
      } finally {
        setInitializing(false);
      }
    }

    loadVault();
  }, [vaultId]);

  // Reset ready flag when vault changes
  useEffect(() => {
    readyRef.current = false;
  }, [vaultId]);

  // Notify parent once when initialization completes; allow immediate if seeded from cache with linked state
  useEffect(() => {
    if (!initializing && !readyRef.current) {
      readyRef.current = true;
      const immediate = seededFromCacheRef.current && (mode === 'synced' || Boolean(linkedRepoUrl));
      onReady?.({ immediate });
    }
  }, [initializing, onReady, mode, linkedRepoUrl]);

  useEffect(() => {
    if (!reindexing) {
      setReindexPercent(0);
      setReindexJobIds(null);
    }
  }, [reindexing]);

  const handleImport = async () => {
    if (!vaultId) {
      toast({ variant: 'destructive', title: 'Error', description: 'Select a vault before importing.' });
      return;
    }

    const trimmedUrl = repoUrl.trim();
    const trimmedToken = personalAccessToken.trim();

    if (!trimmedUrl) {
      toast({ variant: 'destructive', title: 'Error', description: 'Enter a Git repository URL to import.' });
      return;
    }

    setLoading(true);

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 30_000);

    try {
      const payload: Record<string, unknown> = { importSourceUrl: trimmedUrl };
      if (trimmedToken) {
        payload.personalAccessToken = trimmedToken;
      }

      const response = await fetch(`/api/vaults/${vaultId}/sync/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        const message = result?.message || result?.error || 'Import failed. Verify the URL and token, then try again.';
        throw new Error(message);
      }

      if (typeof result?.detectedSourceWiki === 'string') {
        setDetectedWiki(result.detectedSourceWiki);
      }

      setRepoUrl('');
      setPersonalAccessToken('');
      toast({
        variant: 'success',
        title: 'Success',
        description: 'Repository imported successfully. Content is now local to this vault.',
      });
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        toast({ variant: 'destructive', title: 'Timeout', description: 'Import timed out. Please try again.' });
      } else {
        toast({
          variant: 'destructive',
          title: 'Import Failed',
          description: error?.message || 'Import failed. Please try again.',
        });
      }
    } finally {
      window.clearTimeout(timeoutId);
      setLoading(false);
    }
  };

  const handleLink = async () => {
    if (!vaultId) {
      toast({ variant: 'destructive', title: 'Error', description: 'Select a vault before linking.' });
      return;
    }
    const trimmedUrl = repoUrl.trim();
    const trimmedToken = personalAccessToken.trim();
    if (!trimmedUrl) {
      toast({ variant: 'destructive', title: 'Error', description: 'Enter a Git repository URL to link.' });
      return;
    }
    setLoading(true);
    try {
      const payload: Record<string, unknown> = { repoUrl: trimmedUrl };
      if (trimmedToken) payload.personalAccessToken = trimmedToken;
      const response = await fetch(`/api/vaults/${vaultId}/sync/link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result?.message || result?.error || 'Failed to link repository');
      }
      setRepoUrl('');
      setPersonalAccessToken('');
      setLinkedRepoUrl(result?.repoUrl || trimmedUrl);
      setMode('synced');
      if (vaultId) {
        setCachedVaultDetails(vaultId, {
          repoUrl: result?.repoUrl || trimmedUrl,
          mode: 'synced',
          detectedSourceWiki:
            typeof result?.detectedSourceWiki === 'string'
              ? result.detectedSourceWiki
              : cached?.detectedSourceWiki ?? null,
        });
      }
      if (typeof result?.detectedSourceWiki === 'string') {
        setDetectedWiki(result.detectedSourceWiki);
      }
      toast({ variant: 'success', title: 'Success', description: 'Repository linked successfully.' });
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Linking Failed',
        description: error?.message || 'Failed to link repository.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = useCallback(async () => {
    if (!vaultId) return;
    setRefreshing(true);
    try {
      const response = await fetch(`/api/vaults/${vaultId}/sync/refresh`, { method: 'POST' });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result?.message || result?.error || 'Failed to refresh');
      if (typeof result?.detectedSourceWiki === 'string') {
        setDetectedWiki(result.detectedSourceWiki);
        setCachedVaultDetails(vaultId, {
          detectedSourceWiki: result.detectedSourceWiki,
        });
      }
      toast({
        variant: 'success',
        title: 'Success',
        description: linkedRepoUrl ? 'Refreshed from remote.' : 'Repository initialized.',
      });
      // Trigger navigation reload so user sees updated content
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('vault:nav-updated'));
      }
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Refresh Failed',
        description: error?.message || 'Failed to refresh.',
      });
    } finally {
      setRefreshing(false);
    }
  }, [vaultId, linkedRepoUrl, toast]);

  const handleUnlink = async () => {
    if (!vaultId) return;
    setLoading(true);
    try {
      const response = await fetch(`/api/vaults/${vaultId}/sync/disconnect`, { method: 'DELETE' });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result?.message || 'Failed to unlink repository');
      setLinkedRepoUrl(null);
      setMode('managed');
      setWebhookUrl(null);
      setHasWebhookSecret(false);
      if (vaultId) {
        setCachedVaultDetails(vaultId, { repoUrl: null, mode: 'managed' });
      }
      toast({ variant: 'success', title: 'Success', description: 'Repository unlinked.' });
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Unlink Failed',
        description: error?.message || 'Failed to unlink repository.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReindex = useCallback(async () => {
    if (!vaultId) return;
    setLoading(true);
    setReindexing(true);
    setReindexPercent(0);
    try {
      const res = await fetch(`/api/vaults/${vaultId}/search/reindex?scope=both&processNow=true&returnJobs=true`, {
        method: 'POST',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to queue full reindex');
      const ids = Array.isArray(data?.jobs) ? data.jobs.map((j: any) => j.id).filter(Boolean) : [];
      if (ids.length > 0) setReindexJobIds(ids);
      toast({ variant: 'success', title: 'Success', description: 'Full reindex queued.' });
    } catch (err: any) {
      toast({
        variant: 'destructive',
        title: 'Reindex Failed',
        description: err?.message || 'Failed to queue full reindex.',
      });
      setReindexing(false);
      setReindexPercent(0);
    } finally {
      setLoading(false);
    }
  }, [vaultId, toast]);

  // Cancel the reindex polling (stops tracking, jobs may still run on server)
  const handleCancelReindex = useCallback(() => {
    setReindexing(false);
    setReindexPercent(0);
    setReindexJobIds(null);
    setReindexStuck(false);
    reindexStartTimeRef.current = null;
    toast({ variant: 'default', title: 'Dismissed', description: 'Stopped tracking progress. Jobs may continue on server.' });
  }, [toast]);

  const fetchActiveJobs = useCallback(async () => {
    const fetchScope = async (scope: 'public' | 'draft') => {
      const params = new URLSearchParams();
      params.set('scope', scope);
      if (vaultSlug) params.set('vault', vaultSlug);
      if (orgSlug) params.set('org', orgSlug);
      const res = await fetch(`/api/search/index/progress?${params.toString()}`, { cache: 'no-store' });
      if (!res.ok) return [];
      const data = await res.json().catch(() => null);
      return Array.isArray(data?.jobs) ? data.jobs : [];
    };

    const [publicRows, draftRows] = await Promise.all([fetchScope('public'), fetchScope('draft')]);
    return [...publicRows, ...draftRows] as Array<{
      id: string;
      scope: 'public' | 'draft';
      status: string;
      phase?: string | null;
      itemsProcessed?: number | null;
      itemsTotal?: number | null;
    }>;
  }, [vaultSlug, orgSlug]);

  useEffect(() => {
    if (!vaultId) return;
    let cancelled = false;

    const checkInitial = async () => {
      try {
        const rows = await fetchActiveJobs();
        if (cancelled) return;
        const active = rows.filter((row) => row && (row.status === 'queued' || row.status === 'running'));
        if (active.length > 0) {
          setReindexing(true);
          setReindexJobIds(active.map((row) => row.id));
          const totals = active.reduce(
            (acc: { processed: number; total: number }, row) => {
              const total = Number(row.itemsTotal ?? 0);
              const processed = Number(row.itemsProcessed ?? 0);
              if ((row.phase === 'embedding' || row.phase === 'embeddings' || row.phase === 'writing' || row.phase === 'reading' || row.phase === 'incremental') && total > 0) {
                acc.processed += processed;
                acc.total += total;
              }
              return acc;
            },
            { processed: 0, total: 0 }
          );
          if (totals.total > 0) {
            setReindexPercent(Math.max(1, Math.min(99, Math.round((totals.processed / totals.total) * 100))));
          } else {
            setReindexPercent(0);
          }
        } else {
          setReindexing(false);
          setReindexJobIds(null);
          setReindexPercent(0);
        }
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[RepositorySync] initial progress fetch failed', error);
        }
      }
    };

    void checkInitial();

    return () => {
      cancelled = true;
    };
  }, [vaultId, fetchActiveJobs]);

  // 5 minute timeout for reindex polling before showing "stuck" state
  const REINDEX_TIMEOUT_MS = 5 * 60 * 1000;

  useEffect(() => {
    if (!reindexing) {
      reindexStartTimeRef.current = null;
      setReindexStuck(false);
      return;
    }
    // Track when reindexing started
    if (reindexStartTimeRef.current === null) {
      reindexStartTimeRef.current = Date.now();
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const poll = async () => {
      try {
        // Check for timeout - show stuck state after 5 minutes
        if (reindexStartTimeRef.current && Date.now() - reindexStartTimeRef.current > REINDEX_TIMEOUT_MS) {
          setReindexStuck(true);
        }

        const rows = await fetchActiveJobs();
        if (cancelled) return;
        let active = rows.filter((row) => row && (row.status === 'queued' || row.status === 'running'));
        if (reindexJobIds && reindexJobIds.length > 0) {
          active = active.filter((row) => reindexJobIds.includes(row.id));
        }

        if (active.length > 0) {
          const totals = active.reduce(
            (acc: { processed: number; total: number }, row) => {
              const total = Number(row.itemsTotal ?? 0);
              const processed = Number(row.itemsProcessed ?? 0);
              if ((row.phase === 'embedding' || row.phase === 'embeddings' || row.phase === 'writing' || row.phase === 'reading' || row.phase === 'incremental') && total > 0) {
                acc.processed += processed;
                acc.total += total;
              }
              return acc;
            },
            { processed: 0, total: 0 }
          );

          if (totals.total > 0) {
            setReindexPercent(Math.max(1, Math.min(99, Math.round((totals.processed / totals.total) * 100))));
          } else {
            setReindexPercent(0);
          }
        } else {
          setReindexing(false);
          setReindexPercent(0);
          setReindexJobIds(null);
          setReindexStuck(false);
          reindexStartTimeRef.current = null;
        }
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[RepositorySync] progress poll failed', error);
        }
      } finally {
        if (!cancelled) {
          timer = setTimeout(poll, 2000);
        }
      }
    };

    void poll();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [reindexing, reindexJobIds, fetchActiveJobs]);

  const handleUpdateCredentials = async () => {
    if (!vaultId) return;
    setLoading(true);
    try {
      const body: Record<string, unknown> = {};
      if (credToken.trim()) body.personalAccessToken = credToken.trim();
      if (credExpiry.trim()) body.credentialsExpiresAt = credExpiry.trim();
      const res = await fetch(`/api/vaults/${vaultId}/sync/credentials`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to update credentials');
      setCredToken('');
      toast({ variant: 'success', title: 'Success', description: 'Credentials updated.' });
    } catch (err: any) {
      toast({
        variant: 'destructive',
        title: 'Update Failed',
        description: err?.message || 'Failed to update credentials.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateWebhookSecret = async () => {
    if (!vaultId) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/vaults/${vaultId}/sync/webhook/secret`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to generate webhook secret');
      setWebhookUrl(data.webhookUrl || null);
      setWebhookSecret(data.secret || null);
      setHasWebhookSecret(Boolean(data.secret));
      toast({ variant: 'success', title: 'Success', description: 'Webhook secret generated. Copy it now.' });
    } catch (err: any) {
      toast({
        variant: 'destructive',
        title: 'Generation Failed',
        description: err?.message || 'Failed to generate webhook secret.',
      });
    } finally {
      setLoading(false);
    }
  };

  // Communicate reindex and refresh state to parent for header action bar
  useEffect(() => {
    onReindexStateChange?.({
      canReindex,
      isReindexing: reindexing,
      reindexPercent,
      isReindexStuck: reindexStuck,
      onReindex: handleReindex,
      onCancelReindex: handleCancelReindex,
      canRefresh,
      isRefreshing: refreshing,
      onRefresh: handleRefresh,
    });
  }, [canReindex, reindexing, reindexPercent, reindexStuck, handleReindex, handleCancelReindex, canRefresh, refreshing, handleRefresh, onReindexStateChange]);

  // Note: We intentionally do NOT clear reindexState on unmount
  // so that refresh/reindex buttons remain visible across all tabs

  // Gate UI rendering after all hooks are declared to preserve hook order
  const showNoVault = !vaultId;
  // Managed mode = git fully managed by Inovisum, user has no external access
  // Synced mode = user's own repo linked, they co-manage it
  const isSystemManaged = mode === 'managed';
  const isLinked = isSystemManaged || mode === 'synced' || Boolean(linkedRepoUrl);

  return (
    <div className='flex h-full flex-col'>
      {showNoVault ? (
        <section className='rounded-xl border border-dashed p-6 text-sm text-muted-foreground'>
          Select a vault from the sidebar to manage repository linking and imports.
        </section>
      ) : (
        <>
          <div className='flex-1 min-h-0 overflow-y-auto p-4 scrollbar-fade'>
            {!isSystemManaged && (
              <>
                <div className='mb-4 flex items-center justify-between gap-2'>
                  <div>
                    <h2 className='text-lg font-semibold'>{isLinked ? 'Linked Repository' : 'Repository Link'}</h2>
                    <p className='text-sm text-muted-foreground'>
                      {isLinked
                        ? 'This vault is linked to a remote repository. You can refresh or unlink.'
                        : 'Link a repository for continuous sync or import a snapshot.'}
                    </p>
                  </div>
                  <Badge variant={detectedWiki === 'azure-wiki' ? 'default' : 'secondary'}>{detectedWikiLabel}</Badge>
                </div>
                <Separator className='mb-6' />
              </>
            )}

            {!isLinked && (
              <div className='space-y-6'>
                <section className='grid gap-4'>
                  <div className='grid gap-2'>
                    <Label htmlFor='repo-url'>Repository URL</Label>
                    <Input
                      id='repo-url'
                      placeholder='e.g. https://dev.azure.com/org/project/_git/wiki'
                      value={repoUrl}
                      onChange={(event) => setRepoUrl(event.target.value)}
                      disabled={loading}
                    />
                  </div>

                  <div className='grid gap-2'>
                    <Label htmlFor='repo-token'>Personal Access Token (optional)</Label>
                    <Input
                      id='repo-token'
                      type='password'
                      placeholder='e.g. (token hidden)'
                      value={personalAccessToken}
                      onChange={(event) => setPersonalAccessToken(event.target.value)}
                      disabled={loading}
                    />
                    <p className='text-xs text-muted-foreground'>
                      Required for private repositories. Tokens are used only for the requested operation and are not
                      displayed.
                    </p>
                  </div>
                </section>

                <div className='rounded-lg bg-muted/50 p-4 text-sm text-muted-foreground'>
                  <h3 className='mb-2 font-medium text-foreground'>How it works</h3>
                  <ul className='list-disc space-y-1 pl-4'>
                    <li>
                      <strong>Link Repository:</strong> Enables continuous sync via webhook/pull.
                    </li>
                    <li>
                      <strong>Import Snapshot:</strong> Performs a one-time copy of the content.
                    </li>
                  </ul>
                </div>
              </div>
            )}

            {isLinked && isSystemManaged && (
              <section className='space-y-6'>
                <div className='flex items-center gap-3'>
                  <Badge variant='default'>Managed</Badge>
                  <h2 className='text-lg font-semibold'>Managed Repository</h2>
                </div>

                <p className='text-sm text-muted-foreground'>
                  This vault&apos;s content is fully managed by Inovisum. We handle the underlying Git repository,
                  synchronization, and infrastructure so you can focus on your content.
                </p>

                <div className='grid gap-4 sm:grid-cols-2'>
                  <Card>
                    <CardHeader>
                      <CardTitle className='text-sm'>What you get</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className='space-y-1.5 text-xs text-muted-foreground'>
                        <li className='flex items-start gap-2'>
                          <span className='mt-0.5 text-green-600'>+</span>
                          <span>Zero configuration required</span>
                        </li>
                        <li className='flex items-start gap-2'>
                          <span className='mt-0.5 text-green-600'>+</span>
                          <span>No credentials to manage or rotate</span>
                        </li>
                        <li className='flex items-start gap-2'>
                          <span className='mt-0.5 text-green-600'>+</span>
                          <span>Automatic backups and versioning</span>
                        </li>
                        <li className='flex items-start gap-2'>
                          <span className='mt-0.5 text-green-600'>+</span>
                          <span>Guaranteed sync reliability</span>
                        </li>
                      </ul>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className='text-sm'>Compared to Linked</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className='space-y-1.5 text-xs text-muted-foreground'>
                        <li className='flex items-start gap-2'>
                          <span className='mt-0.5 text-amber-600'>~</span>
                          <span>No external Git access</span>
                        </li>
                        <li className='flex items-start gap-2'>
                          <span className='mt-0.5 text-amber-600'>~</span>
                          <span>Edit only through Inovisum</span>
                        </li>
                        <li className='flex items-start gap-2'>
                          <span className='mt-0.5 text-green-600'>+</span>
                          <span>Lower risk of sync conflicts</span>
                        </li>
                        <li className='flex items-start gap-2'>
                          <span className='mt-0.5 text-green-600'>+</span>
                          <span>Simplified access control</span>
                        </li>
                      </ul>
                    </CardContent>
                  </Card>
                </div>

                <div className='rounded-lg border border-dashed border-brand/30 bg-brand/5 p-4'>
                  <div className='flex items-center gap-2 text-sm font-medium text-brand'>
                    <span>On the roadmap</span>
                  </div>
                  <p className='mt-1 text-xs text-brand/70'>
                    Mode migration will allow you to switch between Managed and Linked modes. Export your managed
                    repository to your own Git provider, or transition a linked repository to fully managed
                    infrastructure.
                  </p>
                </div>

                <div className='rounded-lg bg-muted/50 p-4'>
                  <h3 className='mb-1 text-sm font-medium'>Search Index</h3>
                  <p className='text-xs text-muted-foreground'>
                    If search results seem outdated or incomplete, you can rebuild the search index. This process runs
                    in the background and typically takes a few minutes.
                  </p>
                </div>
              </section>
            )}

            {isLinked && !isSystemManaged && (
              <section className='space-y-4'>
                <div className='flex flex-col gap-2'>
                  <Label>Linked URL</Label>
                  <Input readOnly value={linkedRepoUrl || ''} className='bg-muted text-muted-foreground' />
                  <p className='text-xs text-muted-foreground'>
                    Personal access token is not shown. Use Refresh to pull latest changes.
                  </p>
                </div>

                <Separator />

                <div className='grid gap-3 sm:grid-cols-2'>
                  <div className='flex flex-col gap-2'>
                    <Label>Update Access Token</Label>
                    <Input
                      type='password'
                      placeholder='New token (optional)'
                      value={credToken}
                      onChange={(e) => setCredToken(e.target.value)}
                    />
                  </div>
                  <div className='flex flex-col gap-2'>
                    <Label>Token Expiry (optional)</Label>
                    <Input type='datetime-local' value={credExpiry} onChange={(e) => setCredExpiry(e.target.value)} />
                  </div>
                  <div className='sm:col-span-2'>
                    <Button onClick={handleUpdateCredentials} disabled={loading || !canWrite}>
                      {loading ? 'Saving…' : 'Update Credentials'}
                    </Button>
                  </div>
                </div>

                <Separator />

                <div className='space-y-2'>
                  <h3 className='text-sm font-medium'>Webhook</h3>
                  <p className='text-xs text-muted-foreground'>
                    Configure a push webhook on your Git provider to auto-sync.
                  </p>
                  <div className='flex flex-col gap-2'>
                    <Label>Webhook URL</Label>
                    <Input readOnly value={webhookUrl || ''} className='bg-muted text-muted-foreground' />
                    {!hasWebhookSecret ? (
                      <Button onClick={handleGenerateWebhookSecret} disabled={loading || !canWrite}>
                        Generate Secret
                      </Button>
                    ) : (
                      <div className='flex items-center gap-2'>
                        <Button onClick={handleGenerateWebhookSecret} disabled={loading || !canWrite} variant='secondary'>
                          Rotate Secret
                        </Button>
                        <p className='text-xs text-muted-foreground'>
                          A new secret will be generated; update your provider.
                        </p>
                      </div>
                    )}
                    {webhookSecret && (
                      <div className='rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900'>
                        Secret (copy now): {webhookSecret}
                      </div>
                    )}
                    <ul className='list-disc space-y-1 pl-5 text-xs text-muted-foreground'>
                      <li>
                        GitHub (and GH Enterprise): Content type &quot;application/json&quot;; set Secret; event: Push.
                        We verify X-Hub-Signature-256.
                      </li>
                      <li>GitLab (self-hosted too): set &quot;Secret Token&quot;; we verify X-Gitlab-Token matches.</li>
                      <li>Bitbucket Cloud/Server: set a webhook secret; we verify X-Hub-Signature (sha256/sha1).</li>
                      <li>Gitea/Gogs: set secret; we verify X-Gitea-Signature / X-Gogs-Signature (HMAC).</li>
                      <li>
                        Azure DevOps: Web Hook/Service Hook with Basic auth. Username arbitrary; Password must be this
                        secret.
                      </li>
                    </ul>
                  </div>
                </div>
              </section>
            )}
          </div>

          <Separator className='shrink-0' />
          <div className='flex shrink-0 items-center justify-end gap-3 bg-background/70 p-4'>
            {isLinked ? (
              <>
                {!isSystemManaged && (
                  <Button size='sm' variant='destructive' onClick={handleUnlink} disabled={loading || !canWrite}>
                    Unlink
                  </Button>
                )}
              </>
            ) : (
              <>
                <Button size='sm' onClick={handleImport} disabled={loading || !isValidUrl || !canWrite}>
                  {loading ? 'Importing…' : 'Import Snapshot'}
                </Button>
                <Button size='sm' onClick={handleLink} disabled={loading || !isValidUrl || !canWrite}>
                  Link Repository
                </Button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default RepositorySync;
